<template>
  <form v-if="info" autocomplete="off" @submit.prevent="onSubmitForm">
    <Alert v-if="errorMsgs.length" type="danger" :messages="errorMsgs"></Alert>

    <div class="b-booking-upsell mb-80">
      <Loader v-if="isLoading" display="absolute"></Loader>

      <div class="b-booking-upsell__header" @click="onClickHeader">
        <div class="b-booking-upsell__text">
          <div class="extrabold highlight">
            {{ dictionary.upsellSubTitle }}
          </div>
          <div class="h2">
            {{ dictionary.upsellTitle }}
            <span class="extrabold">
              {{ dictionary.upsellDailyRatePrefix }}
              <span>{{ convertPriceToMiles(info.dailyRateDifference) }}</span>
              <!-- prettier-ignore -->
              <template v-if="toggleMiles">&nbsp;{{ mbDictionary.milesNBonusBannerSufix }}</template>
              {{ dictionary.upsellDailyRateSuffix }}
            </span>
          </div>

          <div v-if="showMilesToggle" class="form-check form-switch">
            <label class="form-check-label" for="toggleMiles" @click.stop>
              <span v-if="toggleMiles" class="is-active">
                {{ mbDictionary.milesNBonusToggleMilesLabel }}
              </span>
              <span v-else>{{ mbDictionary.milesNBonusToggleEuroLabel }}</span>
            </label>
            <input
              id="toggleMiles"
              v-model="toggleMiles"
              class="form-check-input"
              type="checkbox"
              role="switch"
              @click.stop
              @change="onToggleMiles" />
          </div>
        </div>
        <div class="b-booking-upsell__buttons">
          <button
            type="submit"
            class="btn btn-primary d-none d-lg-block"
            @click.stop>
            {{ dictionary.upsellButtonSubmitText }}
          </button>

          <button
            type="button"
            class="btn dropdown-toggle"
            :class="{ 'is-active': isActive }"></button>
        </div>
      </div>

      <div v-show="isActive" v-auto-animate class="b-booking-upsell__body">
        <div class="b-booking-upsell__vehicle">
          <div class="b-vehicle view-grid">
            <div class="b-vehicle__body">
              <div class="b-vehicle__picture">
                <LazyImage v-if="image" :image="image" />
              </div>

              <button
                type="submit"
                class="btn btn-primary btn-full-width d-flex d-lg-none"
                @click.stop>
                {{ dictionary.upsellButtonSubmitText }}
              </button>

              <div class="b-vehicle__header">
                <div class="b-vehicle__title h4 extrabold">
                  {{ vehicle.Model }}
                </div>
                <div class="b-vehicle__groups mb-30 semibold">
                  <ul>
                    <li data-bs-toggle="tooltip" :title="vehicle.AcrissData">
                      {{ vehicle.GroupAcrissTxt }}
                      <span>
                        <LazyImage
                          :image="{
                            url: '/imgs/icons/tooltip.svg',
                            alt: 'Tooltip',
                          }" />
                      </span>
                    </li>
                    <li class="separator"></li>
                    <li>{{ vehicle.CategoryNames }}</li>
                  </ul>
                </div>
                <div class="b-vehicle__details">
                  <ul>
                    <li>
                      <div class="pair bold">
                        <i class="icon-passenger"></i>
                        {{ vehicle.PassengersTxt }}
                      </div>
                    </li>

                    <li>
                      <div class="b-vehicle__luggages">
                        <div
                          v-if="vehicle.BigLuggage"
                          class="pair bold"
                          data-bs-toggle="tooltip"
                          :title="vehicle.BigLuggageTxt">
                          <i class="icon-suitcase-large"></i>
                          <span>x{{ vehicle.BigLuggage }}</span>
                        </div>

                        <div
                          v-if="vehicle.SmallLuggage"
                          class="pair bold"
                          data-bs-toggle="tooltip"
                          :title="vehicle.SmallLuggageTxt">
                          <i class="icon-suitcase"></i>
                          <span>x{{ vehicle.SmallLuggage }}</span>
                        </div>
                      </div>
                    </li>
                    <li>
                      <div class="pair bold">
                        <i class="icon-ac"></i>
                        <span v-if="vehicle.HasAC">
                          {{ dictionary.acYes }}
                        </span>
                        <span v-else>{{ dictionary.acNo }}</span>
                      </div>
                    </li>
                    <li v-if="showTransmission">
                      <div class="pair bold">
                        <i class="icon-transmission"></i>
                        <span v-if="vehicle.IsManual">
                          {{ dictionary.transmissionManual }}
                        </span>
                        <span v-if="vehicle.IsAutomatic">
                          {{ dictionary.transmissionAutomatic }}
                        </span>
                      </div>
                    </li>
                    <li v-if="vehicle.Extras">
                      <div class="pair">
                        <i class="icon-info"></i>
                        <span>
                          {{ vehicle.Extras }}
                        </span>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>

              <div class="b-vehicle__extras">
                <p>
                  <small>{{ dictionary.bookingIncludes }}</small>
                </p>
                <ul>
                  <li v-for="(extra, index) in vehicle.ExtrasList" :key="index">
                    {{ extra }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import axios from 'axios'
import LazyImage from './common/LazyImage.vue'
import Loader from './common/Loader.vue'
import Alert from './common/Alert.vue'
import * as lib from '../../lib/lib'

export default {
  components: { Loader, Alert, LazyImage },
  props: {
    info: {
      required: true,
      type: Object,
    },
    isPrepaid: {
      required: false,
      type: Boolean,
    },
  },
  data() {
    return {
      isLoading: false,
      errorMsgs: [],
      isActive: false,

      mbDictionary: {},
      milesNBonusLoggedIn: false,
      showMilesNBonus: lib.getData('milesNBonusData'),
      redeemAllowed: false,
      toggleMiles: false,
      rate: 1,
      lang: 'en',
    }
  },
  computed: {
    showMilesToggle() {
      return (
        this.milesNBonusLoggedIn &&
        this.showMilesNBonus &&
        this.isPrepaid &&
        this.redeemAllowed
      )
    },
    vehicle() {
      return this.info.vehicle
    },

    showTransmission() {
      return this.vehicle.IsManual || this.vehicle.IsAutomatic
    },
    image() {
      const images = this.vehicle.Images
      if (!images.length) {
        return null
      }
      return {
        url: images[0],
        alt: '',
      }
    },
  },
  mounted() {
    this.isActive = !lib.scrDevices()

    this.lang = lib.getLocaleFromLangAttr()
    if (lib.getData('milesNBonusData')) {
      const { dictionary, configuration } = lib.getData('milesNBonusData')
      this.mbDictionary = dictionary
      this.milesNBonusLoggedIn = configuration.milesNBonusLoggedIn
      this.redeemAllowed = configuration.redeemAllowed
      this.rate = configuration.rate

      if (!configuration.destinationAllowed) {
        this.showMilesNBonus = false
      }
    }
    this.setEventListeners()
  },
  methods: {
    convertPriceToMiles(price) {
      if (!this.toggleMiles) {
        return price
      }
      const priceWithoutSymbol = price.replace(',', '.').replace('€', '')
      return Math.ceil(priceWithoutSymbol * this.rate).toLocaleString(this.lang)
    },

    setEventListeners() {
      document.addEventListener('milesNBonusLogStatus', (e) => {
        this.milesNBonusLoggedIn = e.detail.logStatus
        this.redeemAllowed = e.detail.redeemAllowed
      })
    },

    onClickHeader() {
      this.isActive = !this.isActive
    },

    onSubmitForm() {
      this.isLoading = true

      const postData = {
        Token: this.info.token,
        GroupName: this.vehicle.Name,
      }
      axios
        .post(this.info.postPageUrl, postData)
        .then((response) => {
          const { HasError, Errors, Url } = response.data

          // response has error
          if (HasError) {
            this.handleError(Errors)
            return false
          }

          window.location.assign(Url)
          this.handleSuccess()
        })
        .catch((errors) => {
          this.handleError([this.dictionary.defaultErrorMessage])
          console.error(errors)
        })
    },
    handleSuccess() {
      this.isLoading = false
      this.errorMsgs = []
    },
    handleError(messages) {
      this.errorMsgs = messages
      this.isLoading = false
    },
  },
}
</script>
